/**
 * Whether the tablet can reach the Worker right now.
 *
 * `navigator.onLine` says whether there is a network interface, which on a
 * quarry with one bar of signal is not the question. The only reliable answer
 * is the last request: `fetch()` rejecting with a TypeError means nothing
 * answered; any response at all — even a 4xx — means the Worker is there. The
 * API client reports both here, and the outbox and the banner read it.
 */

type Listener = (online: boolean) => void;

let online = true;
const listeners = new Set<Listener>();

export function isOnline(): boolean {
  return online;
}

export function subscribeConnectivity(listener: Listener): () => void {
  listeners.add(listener);
  listener(online);
  return () => listeners.delete(listener);
}

function set(next: boolean): void {
  if (online === next) return;
  online = next;
  for (const listener of listeners) listener(online);
}

export const noteOnline = (): void => set(true);
export const noteOffline = (): void => set(false);

/** A request that never reached a server, as opposed to one that was refused. */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof Error && err.name === 'AbortError');
}

// The browser's own signal is worth a prompt retry, nothing more.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => set(true));
  window.addEventListener('offline', () => set(false));
}
